import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CandidateCard } from "@/components/CandidateCard";
import { AlertsSignup } from "@/components/AlertsSignup";
import { OntarioMapView } from "@/components/OntarioMapView";
import { WitnessStatsCard } from "@/components/WitnessStatsCard";
import { CommunityPulse } from "@/components/CommunityPulse";
import { ReportErrorButton } from "@/components/ReportError";
import { api } from "@/lib/api";
import {
  Landmark, MapPin, Users, ShieldCheck, ExternalLink, CalendarDays, Newspaper, CalendarClock, AlertCircle,
} from "lucide-react";

export default function OntarioMunicipality() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [wyvCandidates, setWyvCandidates] = useState([]);

  useEffect(() => {
    setData(null);
    setError(null);
    setWyvCandidates([]);
    api.get(`/public/ontario/municipality/${slug}`)
      .then((r) => {
        setData(r.data);
        if (r.data.wyv?.municipality) {
          api.get("/public/candidates").then((cr) => {
            setWyvCandidates(cr.data.filter((c) => c.municipality?.id === r.data.wyv.municipality.id));
          }).catch(() => {});
        }
      })
      .catch((e) => setError(e.response?.status === 404 ? "Municipality not found." : "Could not load this page."));
  }, [slug]);

  if (error) {
    return (
      <div className="wyv-container py-16">
        <Card className="max-w-lg"><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {error} <Link to="/ontario" className="text-primary font-medium hover:underline ml-1">Browse municipalities</Link>
        </CardContent></Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="wyv-container py-16 grid lg:grid-cols-2 gap-6">
        <Skeleton className="h-96" /><div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-48" /></div>
      </div>
    );
  }

  const m = data.municipality;
  const wd = data.ward_data;

  return (
    <div className="wyv-container py-12 sm:py-16">
      <nav className="text-xs text-muted-foreground mb-4" aria-label="Breadcrumb">
        <Link to="/ontario" className="hover:text-foreground hover:underline">Ontario Municipalities</Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground font-medium">{m.name}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="muni-page-title">{m.name}</h1>
          <p className="text-muted-foreground mt-1.5">
            {m.type}{m.upper_tier ? ` · Part of ${m.upper_tier}` : ""}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {wd.available ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="muni-ward-badge">
                <MapPin className="h-3 w-3 mr-1" /> {wd.ward_count} ward boundaries loaded
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300" data-testid="muni-ward-needed-badge">
                Ward data needed — not yet available from open sources
              </Badge>
            )}
            {data.wyv?.municipality && (
              <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                <ShieldCheck className="h-3 w-3 mr-1" /> Witness Your Vote active
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild className="bg-accent hover:bg-accent/90" data-testid="muni-find-ward">
            <Link to="/elections/find-my-ward"><MapPin className="h-4 w-4 mr-1.5" /> Find My Ward</Link>
          </Button>
          <Button asChild variant="outline"><Link to="/elections/map">Ontario Map</Link></Button>
          <ReportErrorButton context={`Municipality page — ${m.name}`} defaultCategory="incorrect_ward_boundary" />
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <OntarioMapView focusMuniSlug={slug} heightClass="h-[360px] sm:h-[480px]" />
          <p className="text-[11px] text-muted-foreground mt-2">
            Boundaries: Land Information Ontario (Open Government Licence – Ontario)
            {wd.available && wd.source ? ` · Wards: ${wd.source}` : ""}. Map © OpenStreetMap contributors.
          </p>

          {wd.available && wd.ward_labels?.length > 0 && (
            <Card className="mt-5" data-testid="muni-wards-card">
              <CardContent className="p-5">
                <h2 className="font-heading font-semibold text-sm text-foreground mb-3">Wards</h2>
                <div className="flex flex-wrap gap-2">
                  {wd.ward_labels.map((w) => (
                    <Badge key={w} variant="outline" className="text-xs">{w}</Badge>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 inline-flex items-center gap-1.5">
                  <CalendarClock className="h-3 w-3" />
                  Boundary data retrieved {wd.retrieved_at ? new Date(wd.retrieved_at).toLocaleDateString("en-CA") : "—"} ·
                  status: {wd.verification_status}
                  {wd.source_page_url && (
                    <a href={wd.source_page_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5 ml-1">
                      source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          {data.wyv?.elections?.length > 0 ? (
            data.wyv.elections.map((e) => (
              <Card key={e.id} data-testid="muni-election-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <h2 className="font-heading font-semibold text-base text-foreground">{e.label}</h2>
                    <Badge variant="outline" className={e.status === "open" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}>
                      {e.status === "open" ? "Declarations open" : e.status}
                    </Badge>
                  </div>
                  {e.voting_opens_at && (
                    <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Voting: {new Date(e.voting_opens_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}
                      {" – "}{e.voting_closes_at ? new Date(e.voting_closes_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : ""}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-5 text-sm text-muted-foreground" data-testid="muni-no-election">
                {m.name} is not yet an active Witness Your Vote municipality.{" "}
                <Link to="/contact" className="text-primary font-medium hover:underline">Request it</Link> or{" "}
                <Link to="/invite-a-candidate" className="text-primary font-medium hover:underline">invite a candidate</Link>.
              </CardContent>
            </Card>
          )}

          {data.wyv?.municipality && (
            <WitnessStatsCard
              municipalityId={data.wyv.municipality.id}
              title={`WitnessYourVote participation — ${m.name}`}
            />
          )}

          {data.wyv?.municipality && (
            <CommunityPulse municipalityId={data.wyv.municipality.id} municipalityName={m.name} />
          )}

          {data.official_sources?.length > 0 && (
            <Card data-testid="muni-official-sources">
              <CardContent className="p-5">
                <h2 className="font-heading font-semibold text-sm text-foreground mb-2">Official sources</h2>
                <ul className="space-y-1.5">
                  {data.official_sources.map((s, i) => (
                    <li key={i} className="text-xs">
                      <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all">
                        {s.source_url} <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                      <span className="text-muted-foreground ml-1">
                        ({s.source_type === "election_news" ? "election page monitor" : "candidate list"}
                        {s.last_checked_at ? ` · checked ${new Date(s.last_checked_at).toLocaleDateString("en-CA")}` : ""})
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {wyvCandidates.length > 0 && (
            <div data-testid="muni-wyv-candidates">
              <h2 className="font-heading font-semibold text-base text-foreground mb-3 inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" /> Participating candidates
              </h2>
              <div className="space-y-3">
                {wyvCandidates.map((c) => <CandidateCard key={c.id} candidate={c} declareMode />)}
              </div>
            </div>
          )}

          {data.imported_candidates?.length > 0 && (
            <Card data-testid="muni-imported-candidates">
              <CardContent className="p-5">
                <h2 className="font-heading font-semibold text-sm text-foreground mb-2 inline-flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" /> Newest candidate filings (official sources)
                </h2>
                <ul className="space-y-2">
                  {data.imported_candidates.slice(0, 12).map((c) => (
                    <li key={c.id} className="text-sm text-foreground flex items-start justify-between gap-2">
                      <span>{c.full_name}{c.office ? ` — ${c.office}` : ""}{c.ward ? ` (${c.ward})` : ""}</span>
                      <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                        Source <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {data.news?.length > 0 && (
            <Card data-testid="muni-news">
              <CardContent className="p-5">
                <h2 className="font-heading font-semibold text-sm text-foreground mb-2 inline-flex items-center gap-2">
                  <Newspaper className="h-4 w-4 text-primary" /> Local election news
                </h2>
                <ul className="space-y-2">
                  {data.news.map((n) => (
                    <li key={n.id}>
                      <Link to={`/news/${n.slug}`} className="text-sm text-foreground hover:text-primary font-medium">{n.title}</Link>
                      <p className="text-[11px] text-muted-foreground">{n.published_at ? new Date(n.published_at).toLocaleDateString("en-CA") : ""}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
        <div>
          <h2 className="font-heading font-semibold text-xl text-foreground">Get alerts for {m.name}</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md">
            New candidates, election notices and important updates for your community. Stored completely
            separately from any declaration data.
          </p>
        </div>
        <AlertsSignup compact defaultMunicipality={m.name} />
      </div>

      <Card className="mt-10 bg-secondary max-w-3xl">
        <CardContent className="p-5 text-xs text-muted-foreground leading-relaxed" data-testid="muni-disclaimer">
          {data.disclaimer}
        </CardContent>
      </Card>
    </div>
  );
}
